import { Separator } from "@radix-ui/react-separator";
import { type Header, type Table } from "@tanstack/react-table";
import {
	type Dispatch,
	type PropsWithChildren,
	type ReactNode,
	type RefObject,
	type SetStateAction,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";

import { InputText } from "@moment/ui";

import { Popover } from "../../PopoverV2";
import { type DataGridColumnDef } from "../useColumnState";
import { type ColumnType } from "../validation/parseColumnType";
import { EditPersonOptions } from "./EditPersonOptions";
import { EditTagOptions } from "./EditTagOptions";
import { StandardColumnOptions } from "./StandardColumnOptions";

export interface EditColumnPopoverProps<T> {
	table: Table<T>;
	columnDef: DataGridColumnDef<unknown, unknown>;
	inferredFieldType: ColumnType | undefined;
	headerID: string;
	header: Header<T, unknown>;
	headerLabel: ReactNode | JSX.Element;
	headerLabelRef: RefObject<HTMLSpanElement>;
	headerLabelValue: string | undefined;
	setHeaderLabelValue: Dispatch<SetStateAction<string | undefined>>;
	columnTitleInputRef: RefObject<HTMLInputElement>;
	onClose: () => void;
}

function headerToAccessorKey(input: string, existingKeys: Set<string>): string {
	const formattedString = input.toLowerCase().trim().replace(/\s+/g, "-");
	let uniqueString = formattedString;
	let counter = 1;

	while (existingKeys.has(uniqueString)) {
		uniqueString = `${formattedString}-${counter}`;
		counter++;
	}

	return uniqueString;
}

export function EditColumnPopover<T>({
	children,
	table,
	columnDef,
	headerID,
	header,
	headerLabel,
	headerLabelRef,
	headerLabelValue,
	setHeaderLabelValue,
	columnTitleInputRef,
	onClose,
}: PropsWithChildren & EditColumnPopoverProps<T>) {
	const { meta } = table.options;

	const updateColumn = meta?.updateColumn;
	const updateColumnData = meta?.updateColumnData;
	const isEditable = typeof updateColumn === "function";

	const [internalHeaderValue, setInternalHeaderValue] = useState(headerLabelValue);

	const existingAccessorKeys = useMemo(() => {
		const existingKeys = new Set<string>();
		table.getAllColumns().forEach((column) => {
			if (
				"accessorKey" in column.columnDef &&
				typeof column.columnDef.accessorKey === "string"
			) {
				existingKeys.add(column.columnDef.accessorKey);
			}
		});
		return existingKeys;
	}, [table]);

	const newAccessorKey = useMemo(() => {
		if (!internalHeaderValue || internalHeaderValue.trim() === headerLabelValue) {
			return undefined;
		}

		return headerToAccessorKey(internalHeaderValue, existingAccessorKeys);
	}, [internalHeaderValue, headerLabelValue, existingAccessorKeys]);

	useEffect(() => {
		const headerLabelText = headerLabelRef.current?.innerText;
		setHeaderLabelValue(headerLabelText);
		setInternalHeaderValue(headerLabelText);
	}, [headerLabel]);

	const updateColumnTitle = useCallback(() => {
		if (!isEditable || headerLabelValue === internalHeaderValue) {
			return;
		}

		updateColumn(headerID, {
			...header.column.columnDef,
			header: internalHeaderValue,
			accessorKey: newAccessorKey,
		} as DataGridColumnDef<unknown, unknown>);
	}, [
		isEditable,
		headerLabelValue,
		internalHeaderValue,
		updateColumn,
		headerID,
		header.column.columnDef,
		newAccessorKey,
	]);

	let options: ReactNode = (
		<StandardColumnOptions
			headerID={headerID}
			columnDef={columnDef}
			updateColumn={updateColumn}
		/>
	);
	switch (columnDef.fieldType) {
		case "Tag":
			options = (
				<EditTagOptions
					headerID={headerID}
					columnDef={columnDef}
					updateColumn={updateColumn}
					updateColumnData={updateColumnData}
				/>
			);
			break;
		case "Person":
			options = (
				<EditPersonOptions
					headerID={headerID}
					columnDef={columnDef}
					updateColumn={updateColumn}
					updateColumnData={updateColumnData}
				/>
			);
	}

	return (
		<Popover.Root
			defaultOpen
			onOpenChange={(open) => {
				if (open) {
					return;
				}

				updateColumnTitle();
				onClose();
			}}
		>
			<Popover.Trigger asChild>{children}</Popover.Trigger>
			<Popover.Portal>
				<Popover.Content sideOffset={4} className="flex min-w-[20rem] flex-col space-y-2">
					<InputText
						ref={columnTitleInputRef}
						size="sm"
						fullWidth
						placeholder="Column title"
						value={internalHeaderValue}
						onChange={(e) => setInternalHeaderValue(e.target.value)}
						onBlur={updateColumnTitle}
						onKeyDown={(e) => {
							e.stopPropagation();
							if (e.key === "Enter") {
								updateColumnTitle();
							} else if (e.key === "Escape") {
								setInternalHeaderValue(headerLabelValue);
								// TODO blur input, currently it commits the change because of onBlur
								// columnTitleInputRef.current?.blur();
							}
						}}
					/>
					{newAccessorKey && (
						<div className="text-sm text-tertiary mt-1">
							⇢ and migrate data from <code className="font-medium">.{headerID}</code>{" "}
							to <code className="font-medium">.{newAccessorKey}</code>
						</div>
					)}
					{options && (
						<div>
							<Separator className="-mx-4 mt-2 border-b" />
						</div>
					)}
					{options}
				</Popover.Content>
			</Popover.Portal>
		</Popover.Root>
	);
}
